/**
 * Global barcode scanner. USB/Bluetooth scanners "type" the code very fast and press Enter.
 * We watch for that burst anywhere on the page and hand the code to `onScan`, EXCEPT:
 *  - while the cursor is in a text box, dropdown or editable area (the person is typing), or
 *  - while a form with unsaved changes is open (any element marked data-unsaved="true").
 * Normal human typing is far slower than a scanner, so it is never mistaken for a scan.
 */
import { useEffect, useRef } from 'react';
import { normalizeScan } from '@civilos/domain';

const MAX_GAP_MS = 45; // scanners send characters ~5–20 ms apart
const MIN_LEN = 5;

function isEditing(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

export const hasUnsavedWork = () => !!document.querySelector('[data-unsaved="true"]');

/** A scanned value that looks like a paper number (092726-1 or EXCO-26-CIVIL-000001). */
export function looksLikePaperNumber(raw: string): string | null {
  const n = normalizeScan(raw);
  return /^\d{6}-\d+$/.test(n) || /^[A-Z0-9]+-\d{2}-[A-Z]+-\d+$/.test(n) ? n : null;
}

export function useGlobalScanner(onScan: (code: string) => void, enabled = true) {
  const buf = useRef('');
  const last = useRef(0);
  const cb = useRef(onScan);
  cb.current = onScan;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      // Typing in a box, or a dialog is open (quick jump, confirm…): those keys aren't a scan.
      if (isEditing(e.target) || hasUnsavedWork() || document.querySelector('[aria-modal="true"]')) {
        buf.current = '';
        return;
      }
      const now = performance.now();
      if (now - last.current > MAX_GAP_MS) buf.current = '';
      last.current = now;
      if (e.key === 'Enter') {
        const code = buf.current;
        buf.current = '';
        if (code.length >= MIN_LEN) {
          e.preventDefault();
          e.stopPropagation();
          cb.current(code);
        }
        return;
      }
      if (e.key.length === 1) buf.current += e.key;
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [enabled]);
}
